import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Skeleton } from '@mui/material';

import { Icon, Menu } from '@components/base';
import useReferencedByQuery from '@hooks/queryHooks/useReferencedByQuery';
import useSearchContext from '@hooks/useSearchContext';
import { LayoutEntityType } from '@models/Content';
import { getPagePath } from 'src/appRoutes';

export type ReferencedByPagesColumnProps = {
  entityId: string;
  entityType: LayoutEntityType;
};

export const ReferencedBy: FC<ReferencedByPagesColumnProps> = ({ entityId, entityType }) => {
  const { t } = useTranslation();
  const { lcSearchLanguage } = useSearchContext();
  const { data: references, isLoading } = useReferencedByQuery(entityType, entityId);

  return references && references.length > 0 ? (
    <Fragment>
      <Divider flexItem orientation='vertical' />

      <Menu>
        <Menu.Link
          color='secondary'
          title={`${t('referencedBy')} ${references
            .map(({ name }) => name?.[lcSearchLanguage])
            .join(', ')
            .replace(/, ([^,]*)$/, ` ${t('and').toLowerCase()} $1`)}`}
          variant='subtitle2'
        >
          {t('references', { count: references.length })}
        </Menu.Link>

        <Menu.Content>
          {references.map(reference =>
            reference.id ? (
              <Menu.Item
                keepMounted
                key={reference.id}
                rightContent={<Icon fontSize='small' name='openInNew' />}
                to={getPagePath(reference.id)}
                {...{ target: '_blank' }}
              >
                {reference.name?.[lcSearchLanguage]}
              </Menu.Item>
            ) : (
              <Menu.Item disabled key={JSON.stringify(reference)}>
                {reference.name?.[lcSearchLanguage] ??
                  t('missingItem', { type: t('contentType.page') })}
              </Menu.Item>
            )
          )}
        </Menu.Content>
      </Menu>
    </Fragment>
  ) : isLoading ? (
    <Skeleton variant='text' />
  ) : null;
};
